import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  useDisclosure,
  useToast,
  Badge,
  HStack,
  Text,
  Switch,
  VStack,
  Select,
  TableContainer,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Divider,
  SimpleGrid,
  Card,
  CardBody,
  Heading,
  Tooltip,
  Checkbox,
  Tag,
  Wrap,
  WrapItem,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Flex,
  Progress,
  Skeleton,
} from '@chakra-ui/react';
import {
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiSettings,
  FiPieChart,
  FiRotateCcw,
  FiPlay,
  FiShuffle,
  FiArrowUp,
  FiArrowDown,
  FiGift,
  FiUser,
  FiClock,
  FiBarChart2,
  FiRefreshCw,
} from 'react-icons/fi';
import axios from 'axios';
import ConfirmModal from '../components/ConformModel/ConfirmModal';

const API_BASE = '/api/v2/admin/lucky-spin';

const DEFAULT_COLORS = [
  '#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A', '#98D8C8',
  '#F7DC6F', '#BB8FCE', '#85C1E9', '#F8B500', '#FF3CAC',
];

const LuckyWheelPreview = ({ segments, settings, isSpinning, onSpinComplete, winningSegment }) => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const currentRotationRef = useRef(0);
  const targetRotationRef = useRef(0);
  const spinStartTimeRef = useRef(0);
  const isAnimatingRef = useRef(false);

  const activeSegments = segments.filter(s => s.isActive);
  const totalSegments = activeSegments.length;
  const segmentAngle = totalSegments > 0 ? (2 * Math.PI) / totalSegments : 0;
  const totalWeight = activeSegments.reduce((sum, s) => sum + s.probability, 0);

  const pickWeightedSegment = useCallback(() => {
    if (totalWeight === 0 || activeSegments.length === 0) return 0;
    let random = Math.random() * totalWeight;
    for (let i = 0; i < activeSegments.length; i++) {
      random -= activeSegments[i].probability;
      if (random <= 0) return i;
    }
    return activeSegments.length - 1;
  }, [activeSegments, totalWeight]);

  const drawWheel = useCallback((rotation = 0) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const size = canvas.width;
    const center = size / 2;
    const radius = center - 10;
    const innerRadius = radius * 0.28;

    ctx.clearRect(0, 0, size, size);

    const bgColor = settings?.wheelBackgroundColor || '#1A1A2E';
    const gradient = ctx.createRadialGradient(center, center, 0, center, center, radius + 5);
    gradient.addColorStop(0, bgColor);
    gradient.addColorStop(1, adjustBrightness(bgColor, -30));
    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.arc(center, center, radius + 5, 0, 2 * Math.PI);
    ctx.fill();

    if (totalSegments === 0) {
      ctx.fillStyle = '#999';
      ctx.font = 'bold 16px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('No segments configured', center, center);
      return;
    }

    for (let i = 0; i < totalSegments; i++) {
      const seg = activeSegments[i];
      const startAngle = rotation + i * segmentAngle - Math.PI / 2;
      const endAngle = startAngle + segmentAngle;

      ctx.beginPath();
      ctx.moveTo(center, center);
      ctx.arc(center, center, radius, startAngle, endAngle);
      ctx.closePath();

      const segGradient = ctx.createRadialGradient(center, center, innerRadius, center, center, radius);
      segGradient.addColorStop(0, adjustBrightness(seg.color, 20));
      segGradient.addColorStop(1, seg.color);
      ctx.fillStyle = segGradient;
      ctx.fill();

      ctx.strokeStyle = 'rgba(255,255,255,0.2)';
      ctx.lineWidth = 2;
      ctx.stroke();

      const labelAngle = startAngle + segmentAngle / 2;
      const labelRadius = innerRadius + (radius - innerRadius) * 0.55;
      const labelX = center + labelRadius * Math.cos(labelAngle);
      const labelY = center + labelRadius * Math.sin(labelAngle);

      ctx.save();
      ctx.translate(labelX, labelY);
      ctx.rotate(labelAngle + Math.PI / 2);
      ctx.fillStyle = seg.textColor || '#FFFFFF';
      ctx.font = `bold ${Math.max(10, Math.min(18, radius / 18))}px sans-serif`;
      ctx.textAlign = 'center';
      ctx.shadowColor = 'rgba(0,0,0,0.5)';
      ctx.shadowBlur = 3;
      ctx.fillText(seg.label || '', 0, 0);
      ctx.font = `${Math.max(9, Math.min(13, radius / 25))}px sans-serif`;
      ctx.fillText(`${seg.coinValue} coins`, 0, 16);
      ctx.restore();

      const iconRadius = innerRadius + (radius - innerRadius) * 0.25;
      const iconX = center + iconRadius * Math.cos(labelAngle);
      const iconY = center + iconRadius * Math.sin(labelAngle);
      if (seg.icon) {
        ctx.beginPath();
        ctx.arc(iconX, iconY, 14, 0, 2 * Math.PI);
        ctx.fillStyle = 'rgba(255,255,255,0.9)';
        ctx.fill();
      }
    }

    ctx.beginPath();
    ctx.arc(center, center, innerRadius, 0, 2 * Math.PI);
    const btnColor = settings?.centerButtonColor || '#FFB347';
    const btnGradient = ctx.createRadialGradient(center, center, 0, center, center, innerRadius);
    btnGradient.addColorStop(0, adjustBrightness(btnColor, 30));
    btnGradient.addColorStop(1, btnColor);
    ctx.fillStyle = btnGradient;
    ctx.fill();
    ctx.strokeStyle = 'rgba(255,255,255,0.5)';
    ctx.lineWidth = 3;
    ctx.stroke();

    ctx.fillStyle = '#FFFFFF';
    ctx.font = `bold ${Math.max(12, Math.min(16, innerRadius / 2.5))}px sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.shadowColor = 'rgba(0,0,0,0.3)';
    ctx.shadowBlur = 2;
    ctx.fillText(settings?.centerButtonText || 'SPIN', center, center);
    ctx.shadowBlur = 0;
    ctx.textBaseline = 'alphabetic';
  }, [activeSegments, totalSegments, segmentAngle, settings]);

  useEffect(() => {
    drawWheel(currentRotationRef.current);
  }, [drawWheel]);

  const spin = useCallback(() => {
    if (isAnimatingRef.current || totalSegments === 0) return;
    isAnimatingRef.current = true;

    const winningIndex = pickWeightedSegment();
    const winningSeg = activeSegments[winningIndex];

    // Segments are drawn from the top (−PI / 2), and the pointer is fixed at
    // that same angle. Calculate the *additional* rotation required from the
    // wheel's current position, rather than an absolute rotation. The previous
    // calculation only worked for the first spin and could stop on a different
    // segment after the wheel had already been spun.
    const segmentCenterAngle = winningIndex * segmentAngle + segmentAngle / 2;
    const desiredRotation = -segmentCenterAngle;
    const startRotation = currentRotationRef.current;
    const normalizeAngle = (angle) => ((angle % (2 * Math.PI)) + (2 * Math.PI)) % (2 * Math.PI);
    const additionalRotation = normalizeAngle(desiredRotation - startRotation);
    const spins = 5 + Math.floor(Math.random() * 3);

    // Do not add landing jitter: the pointer must finish at the centre of the
    // exact segment that was selected by the system.
    targetRotationRef.current = startRotation + spins * 2 * Math.PI + additionalRotation;
    spinStartTimeRef.current = performance.now();

    const duration = 4500;

    const animate = (now) => {
      const elapsed = now - spinStartTimeRef.current;
      const t = Math.min(elapsed / duration, 1);
      const eased = easeOutCubic(t);

      currentRotationRef.current = startRotation + (targetRotationRef.current - startRotation) * eased;
      drawWheel(currentRotationRef.current);

      if (t < 1) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        isAnimatingRef.current = false;
        if (onSpinComplete) onSpinComplete(winningSeg);
      }
    };
    animationRef.current = requestAnimationFrame(animate);
  }, [totalSegments, pickWeightedSegment, activeSegments, segmentAngle, drawWheel, onSpinComplete]);

  useEffect(() => {
    if (isSpinning) {
      spin();
    }
  }, [isSpinning, spin]);

  useEffect(() => {
    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, []);

  const pointerColor = settings?.pointerColor || '#FF3B30';

  return (
    <VStack spacing={4} align="center">
      <Box
        position="relative"
        w={{ base: '300px', md: '400px', lg: '450px' }}
        h={{ base: '300px', md: '400px', lg: '450px' }}
      >
        <Box
          position="absolute"
          top="4px"
          left="50%"
          transform="translateX(-50%)"
          zIndex="10"
        >
          <svg width="40" height="50" viewBox="0 0 40 50">
            <defs>
              <linearGradient id="pointerGrad" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor={adjustBrightness(pointerColor, 30)} />
                <stop offset="100%" stopColor={pointerColor} />
              </linearGradient>
            </defs>
            <polygon
              points="20,50 2,5 20,18 38,5"
              fill="url(#pointerGrad)"
              stroke="white"
              strokeWidth="2.5"
              filter="drop-shadow(0 4px 6px rgba(0,0,0,0.3))"
            />
            <circle cx="20" cy="12" r="4" fill="white" />
          </svg>
        </Box>

        <canvas
          ref={canvasRef}
          width={450}
          height={450}
          style={{
            width: '100%',
            height: '100%',
            filter: 'drop-shadow(0 10px 30px rgba(0,0,0,0.25))',
            borderRadius: '50%',
          }}
        />
      </Box>

      {winningSegment && (
        <Box
          px={6}
          py={3}
          bg={winningSegment.color}
          color={winningSegment.textColor || 'white'}
          rounded="full"
          fontWeight="bold"
          fontSize="lg"
          shadow="lg"
          border="3px solid white"
        >
          🎉 You won: {winningSegment.label} — {winningSegment.coinValue} coins!
        </Box>
      )}

      {settings?.wheelTitle && (
        <Text fontSize="2xl" fontWeight="bold" color="var(--primary-solid)" textAlign="center">
          {settings.wheelTitle}
        </Text>
      )}

      <Button
        size="lg"
        bg="var(--primary-solid)"
        color="white"
        _hover={{ opacity: 0.9 }}
        leftIcon={<FiPlay />}
        onClick={spin}
        isDisabled={isAnimatingRef.current || totalSegments === 0}
        fontWeight="bold"
        px={10}
        py={6}
        fontSize="lg"
        rounded="full"
        shadow="lg"
      >
        {settings?.spinButtonText || 'SPIN NOW!'}
      </Button>
    </VStack>
  );
};

function adjustBrightness(hex, percent) {
  const num = parseInt(hex?.replace('#', '') || '000000', 16);
  const amt = Math.round(2.55 * percent);
  const R = clamp((num >> 16) + amt, 0, 255);
  const G = clamp(((num >> 8) & 0x00ff) + amt, 0, 255);
  const B = clamp((num & 0x0000ff) + amt, 0, 255);
  return `#${(0x1000000 + R * 0x10000 + G * 0x100 + B).toString(16).slice(1)}`;
}

function clamp(v, min, max) {
  return Math.min(Math.max(v, min), max);
}

function easeOutCubic(t) {
  return 1 - Math.pow(1 - t, 3);
}

const SpinManager = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [segments, setSegments] = useState([]);
  const [settings, setSettings] = useState(null);
  const [history, setHistory] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState({ segments: false, settings: false, history: false, analytics: false });
  const [historyPage, setHistoryPage] = useState(1);
  const [historyPagination, setHistoryPagination] = useState(null);
  const [historySummary, setHistorySummary] = useState(null);
  const [previewSpinning, setPreviewSpinning] = useState(false);
  const [previewWinner, setPreviewWinner] = useState(null);

  const [segmentModal, setSegmentModal] = useState({ open: false, editing: null });
  const [settingsDirty, setSettingsDirty] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, segment: null });
  const [reverseConfirm, setReverseConfirm] = useState({ open: false, spin: null });

  const [segmentForm, setSegmentForm] = useState({
    label: '', coinValue: 10, color: DEFAULT_COLORS[0], textColor: '#FFFFFF',
    probability: 10, position: 0, icon: '', isActive: true,
  });

  const [settingsForm, setSettingsForm] = useState({
    dailySpinsLimit: 1, cooldownHours: 18, allowedGenders: ['male'],
    wheelTitle: 'Daily Spin', spinButtonText: 'SPIN NOW!',
    wheelBackgroundColor: '#1A1A2E', pointerColor: '#FF3B30',
    centerButtonColor: '#FFB347', centerButtonText: 'SPIN', isEnabled: true,
  });

  const toast = useToast();

  const fetchSegments = async () => {
    try {
      setLoading(l => ({ ...l, segments: true }));
      const res = await axios.get(`${API_BASE}/segments?includeInactive=true`);
      if (res.data.status === 'OK') {
        setSegments(res.data.data.segments || []);
      }
    } catch (e) {
      console.error(e);
      toast({ title: 'Failed to load segments', status: 'error', duration: 3000 });
    } finally {
      setLoading(l => ({ ...l, segments: false }));
    }
  };

  const fetchSettings = async () => {
    try {
      setLoading(l => ({ ...l, settings: true }));
      const res = await axios.get(`${API_BASE}/settings`);
      if (res.data.status === 'OK') {
        const s = res.data.data.settings;
        setSettings(s);
        setSettingsForm({
          dailySpinsLimit: s.dailySpinsLimit ?? 1,
          cooldownHours: s.cooldownHours ?? 18,
          allowedGenders: s.allowedGenders ?? ['male'],
          wheelTitle: s.wheelTitle ?? 'Daily Spin',
          spinButtonText: s.spinButtonText ?? 'SPIN NOW!',
          wheelBackgroundColor: s.wheelBackgroundColor ?? '#1A1A2E',
          pointerColor: s.pointerColor ?? '#FF3B30',
          centerButtonColor: s.centerButtonColor ?? '#FFB347',
          centerButtonText: s.centerButtonText ?? 'SPIN',
          isEnabled: s.isEnabled ?? true,
        });
        setSettingsDirty(false);
      }
    } catch (e) {
      console.error(e);
      toast({ title: 'Failed to load settings', status: 'error', duration: 3000 });
    } finally {
      setLoading(l => ({ ...l, settings: false }));
    }
  };

  const fetchHistory = async (page = 1) => {
    try {
      setLoading(l => ({ ...l, history: true }));
      const res = await axios.get(`${API_BASE}/history`, { params: { page, limit: 20 } });
      if (res.data.status === 'OK') {
        setHistory(res.data.data.history || []);
        setHistoryPagination(res.data.data.pagination);
        setHistorySummary(res.data.data.summary);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(l => ({ ...l, history: false }));
    }
  };

  const fetchAnalytics = async () => {
    try {
      setLoading(l => ({ ...l, analytics: true }));
      const res = await axios.get(`${API_BASE}/analytics`);
      if (res.data.status === 'OK') {
        setAnalytics(res.data.data);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(l => ({ ...l, analytics: false }));
    }
  };

  useEffect(() => {
    fetchSegments();
    fetchSettings();
    fetchHistory(1);
    fetchAnalytics();
  }, []);

  useEffect(() => {
    if (activeTab === 2 && history.length === 0) fetchHistory(1);
    if (activeTab === 3 && !analytics) fetchAnalytics();
  }, [activeTab]);

  const handleOpenSegmentModal = (seg = null) => {
    setSegmentModal({ open: true, editing: seg });
    if (seg) {
      setSegmentForm({
        label: seg.label || '',
        coinValue: seg.coinValue ?? 10,
        color: seg.color || DEFAULT_COLORS[0],
        textColor: seg.textColor || '#FFFFFF',
        probability: seg.probability ?? 10,
        position: seg.position ?? segments.length,
        icon: seg.icon || '',
        isActive: seg.isActive ?? true,
      });
    } else {
      setSegmentForm({
        label: '', coinValue: 10, color: DEFAULT_COLORS[segments.length % DEFAULT_COLORS.length],
        textColor: '#FFFFFF', probability: 10, position: segments.length,
        icon: '', isActive: true,
      });
    }
  };

  const handleSegmentSubmit = async () => {
    try {
      const payload = { ...segmentForm };
      if (!payload.icon) delete payload.icon;
      if (segmentModal.editing) {
        const res = await axios.put(`${API_BASE}/segments/${segmentModal.editing._id}`, payload);
        if (res.data.status === 'OK') {
          toast({ title: 'Segment updated', status: 'success', duration: 2500 });
          setPreviewWinner(null);
        }
      } else {
        const res = await axios.post(`${API_BASE}/segments`, payload);
        if (res.data.status === 'OK') {
          toast({ title: 'Segment created', status: 'success', duration: 2500 });
          setPreviewWinner(null);
        }
      }
      setSegmentModal({ open: false, editing: null });
      fetchSegments();
    } catch (e) {
      console.error(e);
      toast({ title: 'Failed to save segment', status: 'error', duration: 3000 });
    }
  };

  const handleDeleteSegment = async () => {
    if (!deleteConfirm.segment) return;
    try {
      const res = await axios.delete(`${API_BASE}/segments/${deleteConfirm.segment._id}`);
      if (res.data.status === 'OK') {
        toast({ title: 'Segment deleted', status: 'success', duration: 2500 });
        setPreviewWinner(null);
      }
    } catch (e) {
      toast({ title: 'Delete failed', status: 'error', duration: 3000 });
    } finally {
      setDeleteConfirm({ open: false, segment: null });
      fetchSegments();
    }
  };

  const handleReorder = async (index, dir) => {
    const ordered = [...segments].sort((a, b) => a.position - b.position);
    const swapWith = dir === 'up' ? index - 1 : index + 1;
    if (swapWith < 0 || swapWith >= ordered.length) return;
    [ordered[index], ordered[swapWith]] = [ordered[swapWith], ordered[index]];
    const orderedIds = ordered.map(s => s._id);
    try {
      const res = await axios.put(`${API_BASE}/segments/reorder/bulk`, { orderedIds });
      if (res.data.status === 'OK') {
        toast({ title: 'Segments reordered', status: 'success', duration: 2000 });
        setPreviewWinner(null);
        fetchSegments();
      }
    } catch (e) {
      toast({ title: 'Reorder failed', status: 'error', duration: 3000 });
    }
  };

  const handleGenerateDefaults = async () => {
    try {
      const res = await axios.post(`${API_BASE}/segments/default/generate`);
      if (res.data.status === 'OK') {
        toast({ title: `Created ${res.data.data.segments.length} default segments`, status: 'success', duration: 3000 });
        setPreviewWinner(null);
        fetchSegments();
      }
    } catch (e) {
      toast({ title: e.response?.data?.error || 'Generate failed', status: 'error', duration: 3500 });
    }
  };

  const handleSettingsChange = (field, value) => {
    setSettingsForm(prev => {
      const next = { ...prev, [field]: value };
      setSettingsDirty(true);
      return next;
    });
  };

  const handleGenderToggle = (gender) => {
    setSettingsForm(prev => {
      const current = prev.allowedGenders || [];
      const hasIt = current.includes(gender);
      const next = hasIt ? current.filter(g => g !== gender) : [...current, gender];
      setSettingsDirty(true);
      return { ...prev, allowedGenders: next };
    });
  };

  const handleSaveSettings = async () => {
    try {
      if ((settingsForm.allowedGenders || []).length === 0) {
        toast({ title: 'Select at least one gender', status: 'warning', duration: 3000 });
        return;
      }
      const res = await axios.put(`${API_BASE}/settings`, settingsForm);
      if (res.data.status === 'OK') {
        setSettings(res.data.data.settings);
        setSettingsDirty(false);
        toast({ title: 'Settings saved', status: 'success', duration: 2500 });
        setPreviewWinner(null);
      }
    } catch (e) {
      console.error(e);
      toast({ title: 'Failed to save settings', status: 'error', duration: 3000 });
    }
  };

  const handleReverseSpin = async () => {
    if (!reverseConfirm.spin) return;
    try {
      const res = await axios.post(`${API_BASE}/history/${reverseConfirm.spin._id}/reverse`, {
        reason: 'Admin manual reversal',
      });
      if (res.data.status === 'OK') {
        toast({ title: 'Spin reversed successfully', status: 'success', duration: 3000 });
        setReverseConfirm({ open: false, spin: null });
        fetchHistory(historyPage);
        fetchAnalytics();
      }
    } catch (e) {
      toast({ title: e.response?.data?.error || 'Reversal failed', status: 'error', duration: 3500 });
    }
  };

  const handlePreviewSpinComplete = (seg) => {
    setPreviewWinner(seg);
    setPreviewSpinning(false);
  };

  const totalWeight = segments.filter(s => s.isActive).reduce((sum, s) => sum + s.probability, 0);

  const sortedSegments = [...segments].sort((a, b) => a.position - b.position);

  return (
    <Box p={6} minH="screen" bg="var(--bg-light)">
      <VStack align="stretch" spacing={6}>

        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          bg="var(--surface)"
          p={6}
          rounded="xl"
          border="2px"
          borderColor="var(--border-light)"
          shadow="sm"
          flexWrap="wrap"
          gap={4}
        >
          <Box>
            <HStack spacing={3}>
              <FiGift size={28} color="var(--primary-solid)" />
              <Heading fontSize="2xl" color="var(--primary-solid)">Lucky Spin Manager</Heading>
              {settings && (
                <Badge colorScheme={settings.isEnabled ? 'green' : 'red'} rounded="full" px={3}>
                  {settings.isEnabled ? 'ENABLED' : 'DISABLED'}
                </Badge>
              )}
            </HStack>
            <Text color="gray.500" mt={1}>
              Configure wheel segments, settings and view spin analytics
            </Text>
          </Box>
          <HStack spacing={3}>
            <Button
              leftIcon={<FiShuffle />}
              variant="outline"
              borderColor="var(--primary-solid)"
              color="var(--primary-solid)"
              onClick={handleGenerateDefaults}
              size="md"
            >
              Generate Defaults
            </Button>
            <Button
              leftIcon={<FiPlus />}
              onClick={() => handleOpenSegmentModal(null)}
              bg="var(--primary-solid)"
              color="white"
              _hover={{ opacity: 0.9 }}
              size="md"
            >
              Add Segment
            </Button>
          </HStack>
        </Box>

        <Card bg="var(--surface)" rounded="2xl" border="2px" borderColor="var(--border-light)" shadow="md" overflow="hidden">
          <CardBody p={0}>
            <Tabs variant="soft-rounded" colorScheme="pink" onChange={(i) => setActiveTab(i)} p={{ base: 3, md: 5 }}>
              <TabList flexWrap="wrap" gap={2} mb={4}>
                <Tab fontWeight="semibold"><HStack spacing={2}><FiPieChart />Preview & Segments</HStack></Tab>
                <Tab fontWeight="semibold"><HStack spacing={2}><FiSettings />Wheel Settings</HStack></Tab>
                <Tab fontWeight="semibold"><HStack spacing={2}><FiClock />Spin History</HStack></Tab>
                <Tab fontWeight="semibold"><HStack spacing={2}><FiBarChart2 />Analytics</HStack></Tab>
              </TabList>

              <TabPanels>

                <TabPanel px={0}>
                  <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={{ base: 4, md: 6 }}>

                    <Box
                      bg="linear-gradient(135deg, #fef7f9 0%, #fff5f8 50%, #fef0f6 100%)"
                      rounded="2xl"
                      p={{ base: 4, md: 8 }}
                      border="1px"
                      borderColor="gray.200"
                      display="flex"
                      flexDirection="column"
                      alignItems="center"
                      justifyContent="center"
                      minH="500px"
                    >
                      <Text fontSize="xs" fontWeight="bold" color="var(--primary-solid)" mb={4} textTransform="uppercase" letterSpacing="wider">
                        App Preview — Live
                      </Text>
                      <LuckyWheelPreview
                        segments={sortedSegments}
                        settings={settingsForm}
                        isSpinning={previewSpinning}
                        onSpinComplete={handlePreviewSpinComplete}
                        winningSegment={previewWinner}
                      />
                      {/* <Button
                        mt={4}
                        size="sm"
                        variant="ghost"
                        leftIcon={<FiRefreshCw />}
                        onClick={() => { setPreviewWinner(null); }}
                        color="var(--primary-solid)"
                      >
                        Reset preview
                      </Button> */}
                    </Box>

                    <Box>
                      <HStack justify="space-between" mb={4}>
                        <HStack>
                          <Heading size="md" color="var(--primary-solid)">Segments</Heading>
                          <Badge rounded="full">{segments.length} total</Badge>
                          <Badge rounded="full" colorScheme="green">{segments.filter(s => s.isActive).length} active</Badge>
                        </HStack>
                        <Text fontSize="sm" color="gray.500">Total weight: {totalWeight}</Text>
                      </HStack>

                      {loading.segments ? (
                        <VStack spacing={3}>
                          {[1, 2, 3, 4].map(i => (
                            <Skeleton key={i} height="60px" rounded="xl" w="100%" />
                          ))}
                        </VStack>
                      ) : sortedSegments.length === 0 ? (
                        <Card rounded="xl" bg="gray.50" border="2px dashed" borderColor="gray.300">
                          <CardBody textAlign="center" py={10}>
                            <FiPieChart size={48} style={{ margin: '0 auto', opacity: 0.4 }} />
                            <Text color="gray.500" mt={3} fontWeight="medium">No segments yet</Text>
                            <Text color="gray.400" fontSize="sm">Add a segment or generate defaults</Text>
                            <HStack justify="center" mt={4} spacing={3}>
                              <Button size="sm" leftIcon={<FiShuffle />} variant="outline" onClick={handleGenerateDefaults}>
                                Generate Defaults
                              </Button>
                              <Button size="sm" leftIcon={<FiPlus />} bg="var(--primary-solid)" color="white" onClick={() => handleOpenSegmentModal(null)}>
                                Add Segment
                              </Button>
                            </HStack>
                          </CardBody>
                        </Card>
                      ) : (
                        <VStack spacing={3} align="stretch" maxH="600px" overflowY="auto" pr={2}>
                          {sortedSegments.map((seg, idx) => {
                            const pct = totalWeight > 0 && seg.isActive
                              ? ((seg.probability / totalWeight) * 100).toFixed(1)
                              : 0;
                            return (
                              <Box
                                key={seg._id}
                                rounded="xl"
                                border="1px"
                                borderColor="gray.200"
                                p={4}
                                bg="white"
                                _hover={{ shadow: 'md', borderColor: 'var(--primary-solid)/40' }}
                                transition="all 0.2s"
                              >
                                <HStack spacing={4}>
                                  <Box
                                    w="14"
                                    h="14"
                                    rounded="xl"
                                    bg={seg.color}
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    color={seg.textColor || 'white'}
                                    fontWeight="bold"
                                    fontSize="lg"
                                    shadow="md"
                                    flexShrink={0}
                                  >
                                    {seg.label}
                                  </Box>
                                  <VStack align="start" flex={1} spacing={1} minW={0}>
                                    <HStack>
                                      <Text fontWeight="bold" fontSize="lg">{seg.label}</Text>
                                      {!seg.isActive && <Badge colorScheme="red">Inactive</Badge>}
                                    </HStack>
                                    <HStack fontSize="sm" color="gray.600" flexWrap="wrap">
                                      <Tag size="sm" variant="subtle" colorScheme="orange">
                                        💰 {seg.coinValue} coins
                                      </Tag>
                                      <Tag size="sm" variant="subtle" colorScheme="blue">
                                        ⚖ Weight: {seg.probability}
                                      </Tag>
                                      <Tag size="sm" variant="subtle" colorScheme="purple">
                                        🎯 {pct}% chance
                                      </Tag>
                                      <Tag size="sm" variant="subtle" colorScheme="gray">
                                        Pos #{seg.position}
                                      </Tag>
                                    </HStack>
                                    <Box w="100%">
                                      <Progress
                                        size="xs"
                                        value={parseFloat(pct)}
                                        colorScheme="pink"
                                        rounded="full"
                                      />
                                    </Box>
                                  </VStack>
                                  <VStack flexShrink={0} spacing={1}>
                                    <HStack>
                                      <IconButton
                                        aria-label="Move up"
                                        icon={<FiArrowUp />}
                                        size="xs"
                                        variant="ghost"
                                        onClick={() => handleReorder(idx, 'up')}
                                        isDisabled={idx === 0}
                                      />
                                      <IconButton
                                        aria-label="Move down"
                                        icon={<FiArrowDown />}
                                        size="xs"
                                        variant="ghost"
                                        onClick={() => handleReorder(idx, 'down')}
                                        isDisabled={idx === sortedSegments.length - 1}
                                      />
                                    </HStack>
                                    <HStack>
                                      <IconButton
                                        aria-label="Edit"
                                        icon={<FiEdit2 />}
                                        size="sm"
                                        variant="ghost"
                                        color="blue.500"
                                        onClick={() => handleOpenSegmentModal(seg)}
                                      />
                                      <IconButton
                                        aria-label="Delete"
                                        icon={<FiTrash2 />}
                                        size="sm"
                                        variant="ghost"
                                        color="red.500"
                                        onClick={() => setDeleteConfirm({ open: true, segment: seg })}
                                      />
                                    </HStack>
                                  </VStack>
                                </HStack>
                              </Box>
                            );
                          })}
                        </VStack>
                      )}
                    </Box>
                  </SimpleGrid>
                </TabPanel>

                <TabPanel px={0}>
                  <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={6}>

                    <Box
                      bg="linear-gradient(135deg, #fef7f9 0%, #fff5f8 50%, #fef0f6 100%)"
                      rounded="2xl"
                      p={{ base: 4, md: 8 }}
                      border="1px"
                      borderColor="gray.200"
                      display="flex"
                      flexDirection="column"
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Text fontSize="xs" fontWeight="bold" color="var(--primary-solid)" mb={4} textTransform="uppercase" letterSpacing="wider">
                        Settings Preview
                      </Text>
                      <LuckyWheelPreview
                        segments={sortedSegments}
                        settings={settingsForm}
                        isSpinning={false}
                        onSpinComplete={() => {}}
                        winningSegment={null}
                      />
                    </Box>

                    <Box bg="white" rounded="2xl" border="1px" borderColor="gray.200" p={6}>
                      <HStack justify="space-between" mb={5}>
                        <Heading size="md" color="var(--primary-solid)">Wheel Configuration</Heading>
                        {settingsDirty && (
                          <Badge colorScheme="orange" rounded="full">Unsaved Changes</Badge>
                        )}
                      </HStack>

                      <VStack spacing={5} align="stretch">

                        <FormControl display="flex" alignItems="center">
                          <FormLabel mb={0} fontWeight="bold">Feature Enabled</FormLabel>
                          <Switch
                            isChecked={settingsForm.isEnabled}
                            onChange={(e) => handleSettingsChange('isEnabled', e.target.checked)}
                            colorScheme="pink"
                            size="lg"
                          />
                        </FormControl>

                        <Divider />

                        <SimpleGrid columns={2} spacing={4}>
                          <FormControl>
                            <FormLabel fontWeight="bold">Daily Spins Limit</FormLabel>
                            <NumberInput
                              value={settingsForm.dailySpinsLimit}
                              onChange={(v) => handleSettingsChange('dailySpinsLimit', Number(v))}
                              min={0}
                            >
                              <NumberInputField rounded="lg" />
                              <NumberInputStepper>
                                <NumberIncrementStepper />
                                <NumberDecrementStepper />
                              </NumberInputStepper>
                            </NumberInput>
                          </FormControl>
                          <FormControl>
                            <FormLabel fontWeight="bold">Cooldown (hours)</FormLabel>
                            <NumberInput
                              value={settingsForm.cooldownHours}
                              onChange={(v) => handleSettingsChange('cooldownHours', Number(v))}
                              min={0}
                            >
                              <NumberInputField rounded="lg" />
                              <NumberInputStepper>
                                <NumberIncrementStepper />
                                <NumberDecrementStepper />
                              </NumberInputStepper>
                            </NumberInput>
                          </FormControl>
                        </SimpleGrid>

                        <FormControl>
                          <FormLabel fontWeight="bold">Allowed Genders</FormLabel>
                          <HStack flexWrap="wrap" gap={2}>
                            {['male', 'female', 'other', 'prefer_not_to_say'].map(g => (
                              <Checkbox
                                key={g}
                                isChecked={(settingsForm.allowedGenders || []).includes(g)}
                                onChange={() => handleGenderToggle(g)}
                                colorScheme="pink"
                              >
                                {g.replace('_', ' ')}
                              </Checkbox>
                            ))}
                          </HStack>
                        </FormControl>

                        <Divider />

                        <FormControl>
                          <FormLabel fontWeight="bold">Wheel Title</FormLabel>
                          <Input
                            value={settingsForm.wheelTitle}
                            onChange={(e) => handleSettingsChange('wheelTitle', e.target.value)}
                            maxLength={100}
                            rounded="lg"
                          />
                        </FormControl>

                        <SimpleGrid columns={2} spacing={4}>
                          <FormControl>
                            <FormLabel fontWeight="bold">Spin Button Text</FormLabel>
                            <Input
                              value={settingsForm.spinButtonText}
                              onChange={(e) => handleSettingsChange('spinButtonText', e.target.value)}
                              maxLength={50}
                              rounded="lg"
                            />
                          </FormControl>
                          <FormControl>
                            <FormLabel fontWeight="bold">Center Button Text</FormLabel>
                            <Input
                              value={settingsForm.centerButtonText}
                              onChange={(e) => handleSettingsChange('centerButtonText', e.target.value)}
                              maxLength={20}
                              rounded="lg"
                            />
                          </FormControl>
                        </SimpleGrid>

                        <Divider />

                        <Heading size="sm" color="gray.600">🎨 Colors</Heading>

                        <SimpleGrid columns={2} spacing={4}>
                          <FormControl>
                            <FormLabel fontWeight="bold">Wheel Background</FormLabel>
                            <Flex gap={2} align="center">
                              <Input
                                type="color"
                                value={settingsForm.wheelBackgroundColor}
                                onChange={(e) => handleSettingsChange('wheelBackgroundColor', e.target.value)}
                                p={1}
                                h={12}
                                rounded="lg"
                              />
                              <Input
                                value={settingsForm.wheelBackgroundColor}
                                onChange={(e) => handleSettingsChange('wheelBackgroundColor', e.target.value)}
                                rounded="lg"
                                fontFamily="monospace"
                                w="36"
                              />
                            </Flex>
                          </FormControl>
                          <FormControl>
                            <FormLabel fontWeight="bold">Pointer Color</FormLabel>
                            <Flex gap={2} align="center">
                              <Input
                                type="color"
                                value={settingsForm.pointerColor}
                                onChange={(e) => handleSettingsChange('pointerColor', e.target.value)}
                                p={1}
                                h={12}
                                rounded="lg"
                              />
                              <Input
                                value={settingsForm.pointerColor}
                                onChange={(e) => handleSettingsChange('pointerColor', e.target.value)}
                                rounded="lg"
                                fontFamily="monospace"
                                w="36"
                              />
                            </Flex>
                          </FormControl>
                          <FormControl>
                            <FormLabel fontWeight="bold">Center Button Color</FormLabel>
                            <Flex gap={2} align="center">
                              <Input
                                type="color"
                                value={settingsForm.centerButtonColor}
                                onChange={(e) => handleSettingsChange('centerButtonColor', e.target.value)}
                                p={1}
                                h={12}
                                rounded="lg"
                              />
                              <Input
                                value={settingsForm.centerButtonColor}
                                onChange={(e) => handleSettingsChange('centerButtonColor', e.target.value)}
                                rounded="lg"
                                fontFamily="monospace"
                                w="36"
                              />
                            </Flex>
                          </FormControl>
                        </SimpleGrid>

                        <Divider />

                        <HStack justify="flex-end" pt={2}>
                          <Button
                            variant="outline"
                            onClick={fetchSettings}
                            isDisabled={loading.settings}
                          >
                            Reset
                          </Button>
                          <Button
                            bg="var(--primary-solid)"
                            color="white"
                            _hover={{ opacity: 0.9 }}
                            onClick={handleSaveSettings}
                            isDisabled={!settingsDirty || (settingsForm.allowedGenders || []).length === 0}
                            isLoading={loading.settings}
                          >
                            Save Settings
                          </Button>
                        </HStack>
                      </VStack>
                    </Box>
                  </SimpleGrid>
                </TabPanel>

                <TabPanel px={0}>
                  <VStack spacing={4} align="stretch">

                    {historySummary && (
                      <SimpleGrid columns={{ base: 1, sm: 3 }} spacing={4}>
                        <Card bg="white" rounded="xl" border="1px" borderColor="gray.200">
                          <CardBody>
                            <Text fontSize="sm" color="gray.500">Total Spins</Text>
                            <Text fontSize="2xl" fontWeight="bold" color="var(--primary-solid)">
                              {historySummary.totalSpins?.toLocaleString()}
                            </Text>
                          </CardBody>
                        </Card>
                        <Card bg="white" rounded="xl" border="1px" borderColor="gray.200">
                          <CardBody>
                            <Text fontSize="sm" color="gray.500">Coins Distributed</Text>
                            <Text fontSize="2xl" fontWeight="bold" color="orange.500">
                              {historySummary.totalCoinsDistributed?.toLocaleString()}
                            </Text>
                          </CardBody>
                        </Card>
                        <Card bg="white" rounded="xl" border="1px" borderColor="gray.200">
                          <CardBody>
                            <Text fontSize="sm" color="gray.500">Avg Coins / Spin</Text>
                            <Text fontSize="2xl" fontWeight="bold" color="purple.500">
                              {historySummary.averageCoinsPerSpin?.toLocaleString()}
                            </Text>
                          </CardBody>
                        </Card>
                      </SimpleGrid>
                    )}

                    <Card bg="white" rounded="xl" border="1px" borderColor="gray.200" overflow="hidden">
                      <Box p={5} borderBottom="1px" borderColor="gray.100" display="flex" justifyContent="space-between" alignItems="center">
                        <Heading size="md" color="var(--primary-solid)">Spin History</Heading>
                        <Button
                          leftIcon={<FiRefreshCw />}
                          size="sm"
                          variant="ghost"
                          onClick={() => fetchHistory(historyPage)}
                          isDisabled={loading.history}
                        >
                          Refresh
                        </Button>
                      </Box>
                      <TableContainer overflowX="auto">
                        <Table variant="simple" size="sm">
                          <Thead bg="gray.50">
                            <Tr>
                              <Th>User</Th>
                              <Th>Segment</Th>
                              <Th>Coins Won</Th>
                              <Th>Status</Th>
                              <Th>Time</Th>
                              <Th textAlign="right">Actions</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {loading.history ? (
                              <Tr><Td colSpan={6} textAlign="center" py={10}>Loading...</Td></Tr>
                            ) : history.length === 0 ? (
                              <Tr><Td colSpan={6} textAlign="center" py={10} color="gray.500">No spin records yet</Td></Tr>
                            ) : history.map(h => (
                              <Tr key={h._id} _hover={{ bg: 'gray.50' }}>
                                <Td>
                                  <HStack>
                                    {h.user?.profileImage ? (
                                      <Box w={8} h={8} rounded="full" bgImage={`url(${h.user.profileImage})`} bgSize="cover" />
                                    ) : (
                                      <Box w={8} h={8} rounded="full" bg="var(--primary-solid)/20" display="flex" alignItems="center" justifyContent="center" fontWeight="bold" color="var(--primary-solid)" fontSize="xs">
                                        {(h.user?.fullName || '?').charAt(0).toUpperCase()}
                                      </Box>
                                    )}
                                    <VStack spacing={0} align="start">
                                      <Text fontWeight="medium" fontSize="sm">{h.user?.fullName || 'Unknown'}</Text>
                                      <Text fontSize="xs" color="gray.500">{h.user?.userId || h.user?._id || ''}</Text>
                                    </VStack>
                                  </HStack>
                                </Td>
                                <Td>
                                  <HStack>
                                    <Box w={5} h={5} rounded="md" bg={h.segmentColor || h.segment?.color || 'gray.300'} />
                                    <Text fontSize="sm">
                                      {h.segmentLabel || h.segment?.label || 'N/A'}
                                    </Text>
                                  </HStack>
                                </Td>
                                <Td>
                                  <Text fontWeight="bold" color="orange.500">
                                    {h.coinsWon?.toLocaleString()}
                                  </Text>
                                </Td>
                                <Td>
                                  <Badge
                                    rounded="full"
                                    px={3}
                                    colorScheme={
                                      h.status === 'CONFIRMED' ? 'green' :
                                      h.status === 'CANCELLED' ? 'red' : 'yellow'
                                    }
                                  >
                                    {h.status}
                                  </Badge>
                                </Td>
                                <Td fontSize="sm" color="gray.500">
                                  {h.createdAt ? new Date(h.createdAt).toLocaleString() : '-'}
                                </Td>
                                <Td textAlign="right">
                                  {h.status === 'CONFIRMED' && (
                                    <Tooltip label="Reverse this spin (deduct coins from user)">
                                      <IconButton
                                        aria-label="Reverse spin"
                                        icon={<FiRotateCcw />}
                                        size="sm"
                                        colorScheme="red"
                                        variant="ghost"
                                        onClick={() => setReverseConfirm({ open: true, spin: h })}
                                      />
                                    </Tooltip>
                                  )}
                                </Td>
                              </Tr>
                            ))}
                          </Tbody>
                        </Table>
                      </TableContainer>
                      {historyPagination && historyPagination.totalPages > 1 && (
                        <Box p={4} borderTop="1px" borderColor="gray.100">
                          <HStack justify="center" spacing={2}>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => { setHistoryPage(p => p - 1); fetchHistory(historyPage - 1); }}
                              isDisabled={!historyPagination.hasPrev}
                            >
                              Previous
                            </Button>
                            <Text fontSize="sm" color="gray.500">
                              Page {historyPagination.currentPage} / {historyPagination.totalPages}
                            </Text>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => { setHistoryPage(p => p + 1); fetchHistory(historyPage + 1); }}
                              isDisabled={!historyPagination.hasNext}
                            >
                              Next
                            </Button>
                          </HStack>
                        </Box>
                      )}
                    </Card>
                  </VStack>
                </TabPanel>

                <TabPanel px={0}>
                  {loading.analytics && !analytics ? (
                    <VStack spacing={4}><Skeleton h="120px" rounded="xl" w="100%" /><Skeleton h="400px" rounded="xl" w="100%" /></VStack>
                  ) : (
                    <VStack spacing={6} align="stretch">

                      <SimpleGrid columns={{ base: 1, sm: 2, lg: 5 }} spacing={4}>
                        <Card bg="white" rounded="xl" border="1px" borderColor="gray.200" shadow="sm">
                          <CardBody>
                            <HStack justify="space-between" mb={2}>
                              <Text fontSize="xs" color="gray.500" textTransform="uppercase" fontWeight="bold">Total Spins</Text>
                              <FiBarChart2 color="var(--primary-solid)" />
                            </HStack>
                            <Text fontSize="2xl" fontWeight="bold" color="var(--primary-solid)">
                              {analytics?.overview?.totalSpins?.toLocaleString() || 0}
                            </Text>
                          </CardBody>
                        </Card>
                        <Card bg="white" rounded="xl" border="1px" borderColor="gray.200" shadow="sm">
                          <CardBody>
                            <HStack justify="space-between" mb={2}>
                              <Text fontSize="xs" color="gray.500" textTransform="uppercase" fontWeight="bold">Coins Given</Text>
                              <FiGift color="#FFB347" />
                            </HStack>
                            <Text fontSize="2xl" fontWeight="bold" color="orange.500">
                              {analytics?.overview?.totalCoinsDistributed?.toLocaleString() || 0}
                            </Text>
                          </CardBody>
                        </Card>
                        <Card bg="white" rounded="xl" border="1px" borderColor="gray.200" shadow="sm">
                          <CardBody>
                            <HStack justify="space-between" mb={2}>
                              <Text fontSize="xs" color="gray.500" textTransform="uppercase" fontWeight="bold">Unique Users</Text>
                              <FiUser color="#4ECDC4" />
                            </HStack>
                            <Text fontSize="2xl" fontWeight="bold" color="teal.500">
                              {analytics?.overview?.uniqueUsersCount?.toLocaleString() || 0}
                            </Text>
                          </CardBody>
                        </Card>
                        <Card bg="white" rounded="xl" border="1px" borderColor="gray.200" shadow="sm">
                          <CardBody>
                            <HStack justify="space-between" mb={2}>
                              <Text fontSize="xs" color="gray.500" textTransform="uppercase" fontWeight="bold">Avg / Spin</Text>
                              <FiPieChart color="#BB8FCE" />
                            </HStack>
                            <Text fontSize="2xl" fontWeight="bold" color="purple.500">
                              {analytics?.overview?.averageCoinsPerSpin?.toLocaleString() || 0}
                            </Text>
                          </CardBody>
                        </Card>
                        <Card bg="white" rounded="xl" border="1px" borderColor="gray.200" shadow="sm">
                          <CardBody>
                            <HStack justify="space-between" mb={2}>
                              <Text fontSize="xs" color="gray.500" textTransform="uppercase" fontWeight="bold">Biggest Win</Text>
                              <FiGift color="#FF3B30" />
                            </HStack>
                            <Text fontSize="2xl" fontWeight="bold" color="red.500">
                              {analytics?.overview?.maxCoinsWon?.toLocaleString() || 0}
                            </Text>
                          </CardBody>
                        </Card>
                      </SimpleGrid>

                      <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={6}>
                        <Card bg="white" rounded="xl" border="1px" borderColor="gray.200" overflow="hidden">
                          <Box p={5} borderBottom="1px" borderColor="gray.100">
                            <Heading size="md" color="var(--primary-solid)">🎯 Segment Breakdown</Heading>
                            <Text fontSize="sm" color="gray.500" mt={1}>How often each segment has won</Text>
                          </Box>
                          <Box p={5}>
                            {!analytics?.segmentBreakdown?.length ? (
                              <Text color="gray.400" textAlign="center" py={8}>No data yet</Text>
                            ) : (
                              <VStack spacing={3} align="stretch">
                                {analytics.segmentBreakdown.map((seg, i) => {
                                  const maxWins = Math.max(...analytics.segmentBreakdown.map(s => s.totalWins), 1);
                                  return (
                                    <Box key={seg._id || i}>
                                      <HStack justify="space-between" mb={1}>
                                        <HStack>
                                          <Box w={4} h={4} rounded="md" bg={seg.segmentColor || DEFAULT_COLORS[i % DEFAULT_COLORS.length]} />
                                          <Text fontWeight="medium">{seg.segmentLabel || 'Unknown'}</Text>
                                        </HStack>
                                        <HStack spacing={3}>
                                          <Badge colorScheme="orange" rounded="full">
                                            {seg.totalWins?.toLocaleString()} wins
                                          </Badge>
                                          <Badge colorScheme="purple" rounded="full">
                                            {seg.avgCoinsPerWin?.toLocaleString()} avg
                                          </Badge>
                                        </HStack>
                                      </HStack>
                                      <Progress
                                        value={(seg.totalWins / maxWins) * 100}
                                        bg="gray.100"
                                        sx={{ '& > div': { backgroundColor: seg.segmentColor || 'var(--primary-solid)' } }}
                                        rounded="full"
                                        h={3}
                                      />
                                      <Text fontSize="xs" color="gray.500" mt={1} textAlign="right">
                                        Total coins: {seg.totalCoins?.toLocaleString()}
                                      </Text>
                                    </Box>
                                  );
                                })}
                              </VStack>
                            )}
                          </Box>
                        </Card>

                        <Card bg="white" rounded="xl" border="1px" borderColor="gray.200" overflow="hidden">
                          <Box p={5} borderBottom="1px" borderColor="gray.100">
                            <Heading size="md" color="var(--primary-solid)">🏆 Top Winners</Heading>
                            <Text fontSize="sm" color="gray.500" mt={1}>Top 10 users by total coins won</Text>
                          </Box>
                          <Box p={5} maxH="500px" overflowY="auto">
                            {!analytics?.topWinners?.length ? (
                              <Text color="gray.400" textAlign="center" py={8}>No data yet</Text>
                            ) : (
                              <VStack spacing={2} align="stretch">
                                {analytics.topWinners.map((w, i) => (
                                  <HStack
                                    key={w.userId || i}
                                    p={3}
                                    rounded="xl"
                                    _hover={{ bg: i === 0 ? 'yellow.50' : 'gray.50' }}
                                    transition="all"
                                    spacing={4}
                                  >
                                    <Text
                                      fontSize="2xl"
                                      fontWeight="bold"
                                      w={10}
                                      textAlign="center"
                                      color={
                                        i === 0 ? '#FFB347' :
                                        i === 1 ? '#9CA3AF' :
                                        i === 2 ? '#CD7F32' : 'gray.400'
                                      }
                                    >
                                      {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `#${i + 1}`}
                                    </Text>
                                    {w.profileImage ? (
                                      <Box w={10} h={10} rounded="full" bgImage={`url(${w.profileImage})`} bgSize="cover" border="2px solid white" shadow="sm" />
                                    ) : (
                                      <Box w={10} h={10} rounded="full" bg="var(--primary-solid)/20" display="flex" alignItems="center" justifyContent="center" fontWeight="bold" color="var(--primary-solid)">
                                        {(w.userName || '?').charAt(0).toUpperCase()}
                                      </Box>
                                    )}
                                    <VStack align="start" flex={1} spacing={0} minW={0}>
                                      <Text fontWeight="bold" isTruncated>{w.userName || 'Unknown User'}</Text>
                                      <HStack fontSize="xs" color="gray.500" flexWrap="wrap">
                                        <Tag size="sm" variant="subtle" colorScheme="pink">{w.totalSpins} spins</Tag>
                                        <Tag size="sm" variant="subtle" colorScheme="yellow">Max: {w.biggestWin?.toLocaleString()}</Tag>
                                      </HStack>
                                    </VStack>
                                    <Text fontWeight="bold" fontSize="lg" color="orange.500">
                                      {w.totalCoins?.toLocaleString()}
                                    </Text>
                                  </HStack>
                                ))}
                              </VStack>
                            )}
                          </Box>
                        </Card>
                      </SimpleGrid>
                    </VStack>
                  )}
                </TabPanel>
              </TabPanels>
            </Tabs>
          </CardBody>
        </Card>
      </VStack>

      <Modal isOpen={segmentModal.open} onClose={() => setSegmentModal({ open: false, editing: null })} size="xl">
        <ModalOverlay backdropFilter="blur(4px)" />
        <ModalContent rounded="2xl">
          <ModalHeader borderBottom="1px" borderColor="gray.100">
            <HStack>
              <FiGift color="var(--primary-solid)" size={20} />
              {segmentModal.editing ? 'Edit Segment' : 'Create New Segment'}
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody py={6}>
            <VStack spacing={4} align="stretch">

              <SimpleGrid columns={2} spacing={4}>
                <FormControl isRequired>
                  <FormLabel fontWeight="bold">Label</FormLabel>
                  <Input
                    value={segmentForm.label}
                    onChange={(e) => setSegmentForm(f => ({ ...f, label: e.target.value }))}
                    placeholder="e.g. 100 Coins"
                    maxLength={50}
                    rounded="lg"
                  />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel fontWeight="bold">Coin Value</FormLabel>
                  <NumberInput
                    value={segmentForm.coinValue}
                    onChange={(v) => setSegmentForm(f => ({ ...f, coinValue: Number(v) }))}
                    min={0}
                  >
                    <NumberInputField rounded="lg" />
                    <NumberInputStepper>
                      <NumberIncrementStepper    color="yellow"
          _hover={{ bg: "gray.100" }}/>
                      <NumberDecrementStepper    color="yellow"
          _hover={{ bg: "gray.100" }} />
                    </NumberInputStepper>
                  </NumberInput>
                </FormControl>
              </SimpleGrid>

              <SimpleGrid columns={2} spacing={4}>
                <FormControl>
                  <FormLabel fontWeight="bold">Segment Color</FormLabel>
                  <Flex gap={2} align="center" flexWrap="wrap">
                    <Input
                      type="color"
                      value={segmentForm.color}
                      onChange={(e) => setSegmentForm(f => ({ ...f, color: e.target.value }))}
                      p={1}
                      h={12}
                      rounded="lg"
                      w="80px"
                    />
                    <Input
                      value={segmentForm.color}
                      onChange={(e) => setSegmentForm(f => ({ ...f, color: e.target.value }))}
                      rounded="lg"
                      fontFamily="monospace"
                      w="120px"
                    />
                    <Wrap>
                      {DEFAULT_COLORS.map(c => (
                        <WrapItem key={c}>
                          <Box
                            w={7}
                            h={7}
                            rounded="md"
                            bg={c}
                            cursor="pointer"
                            border={segmentForm.color === c ? '3px solid var(--primary-solid)' : '2px solid transparent'}
                            onClick={() => setSegmentForm(f => ({ ...f, color: c }))}
                            shadow="sm"
                          />
                        </WrapItem>
                      ))}
                    </Wrap>
                  </Flex>
                </FormControl>
                <FormControl>
                  <FormLabel fontWeight="bold">Text Color</FormLabel>
                  <Flex gap={2} align="center">
                    <Input
                      type="color"
                      value={segmentForm.textColor}
                      onChange={(e) => setSegmentForm(f => ({ ...f, textColor: e.target.value }))}
                      p={1}
                      h={12}
                      rounded="lg"
                      w="80px"
                    />
                    <Input
                      value={segmentForm.textColor}
                      onChange={(e) => setSegmentForm(f => ({ ...f, textColor: e.target.value }))}
                      rounded="lg"
                      fontFamily="monospace"
                      w="120px"
                    />
                    <HStack spacing={2}>
                      <Box
                        w={7}
                        h={7}
                        rounded="md"
                        bg="#FFFFFF"
                        cursor="pointer"
                        border={segmentForm.textColor === '#FFFFFF' ? '3px solid var(--primary-solid)' : '1px solid #ccc'}
                        onClick={() => setSegmentForm(f => ({ ...f, textColor: '#FFFFFF' }))}
                      />
                      <Box
                        w={7}
                        h={7}
                        rounded="md"
                        bg="#000000"
                        cursor="pointer"
                        border={segmentForm.textColor === '#000000' ? '3px solid var(--primary-solid)' : '1px solid #ccc'}
                        onClick={() => setSegmentForm(f => ({ ...f, textColor: '#000000' }))}
                      />
                    </HStack>
                  </Flex>
                </FormControl>
              </SimpleGrid>

              <SimpleGrid columns={2} spacing={4}>
                <FormControl isRequired>
                  <FormLabel fontWeight="bold">
                    Probability Weight
                    <Tooltip label="Higher weight = higher chance of winning. Relative to total weight of all segments.">
                      <span style={{ marginLeft: 6, opacity: 0.6 }}>?</span>
                    </Tooltip>
                  </FormLabel>
                  <NumberInput
                    value={segmentForm.probability}
                    onChange={(v) => setSegmentForm(f => ({ ...f, probability: Number(v) }))}
                    min={1}
                  >
                    <NumberInputField rounded="lg" />
                    <NumberInputStepper>
                      <NumberIncrementStepper    color="yellow"
          _hover={{ bg: "gray.100" }}/>
                      <NumberDecrementStepper   color="yellow"
          _hover={{ bg: "gray.100" }} />
                    </NumberInputStepper>
                  </NumberInput>
                  {totalWeight > 0 && (
                    <Text fontSize="xs" color="gray.500" mt={1}>
                      Actual probability: {segmentForm.isActive ? (((segmentForm.probability || 0) / totalWeight) * 100).toFixed(2) : 0}%
                    </Text>
                  )}
                </FormControl>
                <FormControl>
                  <FormLabel fontWeight="bold">Position</FormLabel>
                  <NumberInput
                    value={segmentForm.position}
                    onChange={(v) => setSegmentForm(f => ({ ...f, position: Number(v) }))}
                    min={0}
                  >
                    <NumberInputField rounded="lg" />
                    <NumberInputStepper>
                      <NumberIncrementStepper   color="yellow"
          _hover={{ bg: "gray.100" }} />
                      <NumberDecrementStepper    color="yellow"
          _hover={{ bg: "gray.100" }}/>
                    </NumberInputStepper>
                  </NumberInput>
                </FormControl>
              </SimpleGrid>

              <FormControl>
                <FormLabel fontWeight="bold">Icon URL (optional)</FormLabel>
                <Input
                  value={segmentForm.icon}
                  onChange={(e) => setSegmentForm(f => ({ ...f, icon: e.target.value }))}
                  placeholder="https://..."
                  rounded="lg"
                />
              </FormControl>

              <FormControl display="flex" alignItems="center">
                <FormLabel mb={0} fontWeight="bold">Active Status</FormLabel>
                <Switch
                  isChecked={segmentForm.isActive}
                  onChange={(e) => setSegmentForm(f => ({ ...f, isActive: e.target.checked }))}
                  colorScheme="pink"
                />
              </FormControl>

              <Box
                bg="linear-gradient(135deg, #fef7f9 0%, #fff5f8 100%)"
                rounded="xl"
                p={4}
                mt={2}
              >
                <Text fontSize="sm" fontWeight="bold" color="var(--primary-solid)" mb={2}>Segment Preview</Text>
                <HStack>
                  <Box
                    w="16"
                    h="16"
                    rounded="2xl"
                    bg={segmentForm.color}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    color={segmentForm.textColor}
                    fontWeight="bold"
                    fontSize="xl"
                    shadow="lg"
                  >
                    {segmentForm.label || '—'}
                  </Box>
                  <VStack align="start" spacing={1}>
                    <Text fontWeight="bold">{segmentForm.label || 'No label'}</Text>
                    <Tag size="sm" variant="subtle" colorScheme="orange">💰 {segmentForm.coinValue} coins</Tag>
                    <Tag size="sm" variant="subtle" colorScheme="blue">⚖ Weight: {segmentForm.probability}</Tag>
                  </VStack>
                </HStack>
              </Box>
            </VStack>
          </ModalBody>
          <ModalFooter borderTop="1px" borderColor="gray.100">
            <Button  mr={3} onClick={() => setSegmentModal({ open: false, editing: null })}>
              Cancel
            </Button>
            <Button
              bg="var(--primary-solid)"
              color="white"
              _hover={{ color: 'red', opacity: 0.9 }}
              onClick={handleSegmentSubmit}
              isDisabled={!segmentForm.label}
            >
              {segmentModal.editing ? 'Update Segment' : 'Create Segment'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <ConfirmModal
        isOpen={deleteConfirm.open}
        title="Delete Segment"
        message={`Are you sure you want to delete segment "${deleteConfirm.segment?.label}"? This cannot be undone.`}
        onConfirm={handleDeleteSegment}
        onCancel={() => setDeleteConfirm({ open: false, segment: null })}
      />

      <ConfirmModal
        isOpen={reverseConfirm.open}
        title="Reverse Spin"
        message={`Reverse this spin of ${reverseConfirm.spin?.coinsWon?.toLocaleString()} coins won by ${reverseConfirm.spin?.user?.fullName || 'user'}? Coins will be deducted from the user's wallet.`}
        onConfirm={handleReverseSpin}
        onCancel={() => setReverseConfirm({ open: false, spin: null })}
      />
    </Box>
  );
};

export default SpinManager;
